import CloseOutlined from '@ant-design/icons/CloseOutlined'
import { Image, StyleSheet, Text, View } from '@react-pdf/renderer'
import {
  AutoComplete,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
} from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import dayjs from 'dayjs'
import chunk from 'lodash/chunk'
import { X } from 'lucide-react'
import { AddendumPages } from 'pages/reporting/reports/rug/components/AddendumPages'
import {
  METHOD_TO_PROCEDURE,
  METHOD_TO_PROCEDURE_TITLE,
} from 'pages/reporting/reports/rug/constants/inspectionInfo'
import {
  DEFAULT_CALIBRATION_STEPS,
  DEFAULT_PAYLOADS,
} from 'pages/reporting/reports/rug/hooks/useCantileverReportData'
import { useComponentNominalRangeString } from 'pages/reporting/reports/rug/hooks/useComponentNominalRangeString'
import { useSensorPayloadStrings } from 'pages/reporting/reports/rug/hooks/useSensorPayloadStrings'
import toka4Image from 'pages/reporting/reports/rug/images/toka-4.png'
import {
  CalibrationBlock,
  DataSheetSectionData,
  RugReportSectionId,
} from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import {
  CalibrationBlockType,
  CalibrationStepName,
  ReportInspectionSurface,
  ReportSurfaceCondition,
  SensorPayloadType,
} from 'pages/reporting/reports/rug/types'
import { MeasurementInput } from 'pages/reporting/shared/components/MeasurementInput'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'pages/reporting/shared/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'pages/reporting/shared/pdfGeneration/components/shared/ReportPage'
import { PDF_COLORS } from 'pages/reporting/shared/pdfGeneration/constants/colors'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { useMemo } from 'react'
import { rUGInspectionRobotInfoSchema } from 'rolodex-ontology-lib-ts'
import styled from 'styled-components'
import {
  GeckoAssetModel,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  TimeString,
} from 'types'
import { autocompleteFilterOption } from 'utils/fde/antD'

interface PayloadSerialNumbers {
  serialNumbers: string[]
}

const DISTANCE_UNIT_OPTIONS = [
  {
    value: GeckoMeasurementUnit.Inches.id,
    label: GeckoMeasurementUnit.Inches.name,
  },
  {
    value: GeckoMeasurementUnit.Millimeters.id,
    label: GeckoMeasurementUnit.Millimeters.name,
  },
]

const CALIBRATION_STEP_OPTIONS = Object.values(CalibrationStepName).map(
  (name) => ({ value: name, label: name })
)

const ROBOT_MODEL_OPTIONS = (
  rUGInspectionRobotInfoSchema._def.right.shape.robotType._def
    .options as Array<{
    _def: { value: string }
  }>
).map((option) => ({
  value: option._def.value,
}))

const INSPECTION_SOFTWARE_OPTIONS = [
  { value: 'Fulcrum' },
  { value: 'Robot Control' },
]

const DEFAULT_SERIAL_NUMBERS_COUNT = 24

const SerialNumberLabel = styled.span`
  display: flex;
  align-items: center;
  padding-right: 0.5rem;
  font-weight: 500;
  min-width: 2.5rem;
`

const PayloadGroupWrapper = styled.div`
  margin-bottom: 1rem;
`

const PayloadCard = styled(Space)`
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 0.375rem;
  padding: 1rem;
  position: relative;
`

const PayloadCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`

const PayloadCardTitle = styled.span`
  font-weight: 500;
`

const RemoveButton = styled(CloseOutlined)`
  cursor: pointer;
`

const RemoveButtonWithPadding = styled(CloseOutlined)`
  padding: 0 0.5rem;
  cursor: pointer;
`

const ActionButton = styled(CantileverButton)`
  width: 100%;
`

const InlineFormRow = styled(Space.Compact)`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  width: 100%;
`

const CalibrationTimeRow = styled(Space)`
  display: flex;
  margin-bottom: 0.5rem;
`

const InlineFormItem = styled(Form.Item)`
  flex: 1;
  margin-bottom: 0;
`

const CompactFormItem = styled(Form.Item)`
  margin-bottom: 0.5rem;
`

const FlushFormItem = styled(Form.Item)`
  margin-bottom: 0;
`

const PayloadFrequencyInput = styled(InputNumber)`
  width: 100%;
`

const CalibrationBlockSerialInput = styled(Input)`
  width: 9.375rem;
`

const DistanceValueInput = styled(InputNumber)`
  width: 6.25rem;
`

const UnitSelect = styled(Select)`
  width: 5rem;
`

const RemoveIcon = styled(X)`
  cursor: pointer;
  margin-left: 0.5rem;
  color: #999;
  flex-shrink: 0;
`

const VerticalStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const InlineFieldGroup = styled(Space.Compact)`
  width: 100%;
`

const ParserModalDescription = styled.div`
  margin-bottom: 1rem;
`

const ParserTextArea = styled(Input.TextArea)`
  font-family: monospace;
`

const DataSheetStyles = StyleSheet.create({
  section: {
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'ultrabold',
    textDecoration: 'underline',
    color: PDF_COLORS.ACCENT,
    marginBottom: 5,
  },
  twoColumnLayout: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  column: {
    flexBasis: 0,
    flexGrow: 1,
  },
  separator: {
    width: 1,
    backgroundColor: 'black',
    marginHorizontal: 8,
  },
  fieldRow: {
    fontSize: 9,
    marginBottom: 3,
    flexDirection: 'row',
  },
  fieldLabel: {
    fontWeight: 'bold',
  },
  fieldValue: {
    color: PDF_COLORS.TEXT_PRIMARY,
  },
  tableHeader: {
    fontSize: 9,
    flexBasis: 0,
    flexGrow: 1,
    marginHorizontal: 8,
    fontWeight: 'bold',
  },
  tableHeaderAccent: {
    fontSize: 9,
    flexBasis: 0,
    flexGrow: 1,
    marginHorizontal: 8,
    fontWeight: 'bold',
    color: PDF_COLORS.ACCENT,
  },
  tableCell: {
    fontSize: 9,
    flexBasis: 0,
    flexGrow: 1,
    marginHorizontal: 20,
  },
  tableCellAccent: {
    fontSize: 9,
    flexBasis: 0,
    flexGrow: 1,
    marginHorizontal: 20,
    color: PDF_COLORS.ACCENT,
  },
  tableContainer: {
    flexDirection: 'row',
  },
  tableColumn: {
    flexBasis: 0,
    flexGrow: 1,
    borderTop: 1,
    borderBottom: 1,
    flexDirection: 'column',
  },
  tableColumnWithBorder: {
    flexBasis: 0,
    flexGrow: 1,
    borderTop: 1,
    borderBottom: 1,
    borderRight: 1,
    flexDirection: 'column',
  },
  tableHeaderRow: {
    flexBasis: 'auto',
    flexGrow: 1,
    borderBottom: 1,
  },
  tableDataRow: {
    flexDirection: 'row',
  },
  payloadContainer: {
    marginBottom: 12,
  },
  payloadTitle: {
    fontSize: 11,
    fontWeight: 'ultrabold',
    color: PDF_COLORS.ACCENT,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  calibrationHeader: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  calibrationColumn: {
    fontSize: 9,
    flexBasis: 0,
    flexGrow: 1,
  },
  calibrationTitle: {
    fontSize: 11,
    fontWeight: 'ultrabold',
    textDecoration: 'underline',
    color: PDF_COLORS.ACCENT,
    flexBasis: 0,
    flexGrow: 1,
  },
  calibrationValue: {
    color: PDF_COLORS.ACCENT,
  },
})

const DataSheetTitle: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => <Text style={DataSheetStyles.sectionTitle}>{children}</Text>

const DataSheetSeparator: React.FC = () => (
  <View style={DataSheetStyles.separator} />
)

const DataSheetListedItem: React.FC<{
  fieldName: string
  fieldValue?: string
}> = ({ fieldName, fieldValue }) => (
  <View style={DataSheetStyles.fieldRow}>
    <Text style={DataSheetStyles.fieldLabel}>{fieldName}</Text>
    <Text> {fieldValue ?? 'N/A'}</Text>
  </View>
)

const getAssetDimensionsString = (
  assetModel: GeckoAssetModel | undefined,
  componentModels: readonly GeckoComponentModel[],
  formatMeasurement: (params: {
    measurement: GeckoMeasurement | undefined
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }) => string | undefined
): string => {
  if (!assetModel || componentModels.length === 0) {
    return 'N/A'
  }

  const firstComponent = componentModels[0]
  const componentType = firstComponent.type

  // For vertical tank shells, show diameter and height
  if (componentType === GeckoComponentModelType.VerticalTankShell) {
    const shell = firstComponent as GeckoComponentModel & {
      radius?: GeckoMeasurement
      height?: GeckoMeasurement
    }
    if (shell.radius && shell.height) {
      const diameter = GeckoMeasurement.CreateFromValue(
        shell.radius.num * 2,
        shell.radius.unit
      )
      return `Diameter: ${formatMeasurement({
        measurement: diameter,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}  |  Height: ${formatMeasurement({
        measurement: shell.height,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`
    }
  }

  if (componentType === GeckoComponentModelType.HorizontalTankShell) {
    const shell = firstComponent as GeckoComponentModel & {
      radius?: GeckoMeasurement
      length?: GeckoMeasurement
    }
    if (shell.radius && shell.length) {
      const diameter = GeckoMeasurement.CreateFromValue(
        shell.radius.num * 2,
        shell.radius.unit
      )
      return `Diameter: ${formatMeasurement({
        measurement: diameter,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}  |  Length: ${formatMeasurement({
        measurement: shell.length,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`
    }
  }

  if (
    componentType === GeckoComponentModelType.LegacyBoilerWalls ||
    componentType === GeckoComponentModelType.HoodStraight ||
    componentType === GeckoComponentModelType.HoodBend
  ) {
    const boiler = firstComponent as GeckoComponentModel & {
      tubes?: { tube_diameter?: number; ctc?: number }
    }
    if (boiler.tubes?.tube_diameter && boiler.tubes?.ctc) {
      const tubeDiameter = GeckoMeasurement.CreateFromValue(
        boiler.tubes.tube_diameter,
        GeckoMeasurementUnit.Inches
      )
      const tubeC2C = GeckoMeasurement.CreateFromValue(
        boiler.tubes.ctc,
        GeckoMeasurementUnit.Inches
      )
      return `Tube Diameter: ${formatMeasurement({
        measurement: tubeDiameter,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}  |  Tube C2C: ${formatMeasurement({
        measurement: tubeC2C,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    }
  }

  if (componentType === GeckoComponentModelType.PipeSection) {
    const pipe = firstComponent as GeckoComponentModel & {
      radius?: GeckoMeasurement
      length?: GeckoMeasurement
    }
    if (pipe.radius && pipe.length) {
      const diameter = GeckoMeasurement.CreateFromValue(
        pipe.radius.num * 2,
        pipe.radius.unit
      )
      return `Diameter: ${formatMeasurement({
        measurement: diameter,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}  |  Length: ${formatMeasurement({
        measurement: pipe.length,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`
    }
  }

  const component = firstComponent as GeckoComponentModel & {
    radius?: GeckoMeasurement
    height?: GeckoMeasurement
    length?: GeckoMeasurement
  }
  if (component.radius && (component.height || component.length)) {
    const diameter = GeckoMeasurement.CreateFromValue(
      component.radius.num * 2,
      component.radius.unit
    )
    const lengthOrHeight = component.height || component.length
    const lengthLabel = component.height ? 'Height' : 'Length'
    return `Diameter: ${formatMeasurement({
      measurement: diameter,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}  |  ${lengthLabel}: ${formatMeasurement({
      measurement: lengthOrHeight,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`
  }

  return 'N/A'
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const {
    formData,
    selectedAssetModel,
    selectedComponentModels,
    componentToNominalRange,
    inspectionMetadata,
    formatMeasurement,
  } = useRugReportOps()

  const mainInfo = useMemo(() => formData.mainInfo, [formData.mainInfo])
  const dataSheetData = useMemo(
    () => formData.dataSheetSectionData,
    [formData.dataSheetSectionData]
  )

  const nominalRangeStr = useComponentNominalRangeString(
    componentToNominalRange
  )

  const dimensionsStr = useMemo(
    () =>
      getAssetDimensionsString(
        selectedAssetModel,
        selectedComponentModels || [],
        formatMeasurement
      ),
    [selectedAssetModel, selectedComponentModels, formatMeasurement]
  )

  const { payloadTypesStr, sensorFrequenciesStr, sensorDiametersStr } =
    useSensorPayloadStrings({
      payloads: dataSheetData?.payloads,
      formatMeasurement,
    })

  const sensorAngle = inspectionMetadata.sensorPayloadMetadata?.angle

  const softwareVersion =
    dataSheetData?.inspectionSoftware ??
    inspectionMetadata.inspectionSoftwareVersion

  const sensorPayloadSerialNumbers = dataSheetData?.sensorSerialNumbers
    ? Object.entries(dataSheetData.sensorSerialNumbers)
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .map((item) => item[1])
    : []

  const calibrationSteps = useMemo(() => {
    return (
      dataSheetData?.calibrationSteps?.map((step) => {
        return {
          stepName: step.stepName,
          distanceMeasurement: formatMeasurement({
            measurement: GeckoMeasurement.CreateFromValue(
              step.distance.value,
              GeckoMeasurementUnit.GetOrCreate(step.distance.unit)
            ),
            variant: 'narrow',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
          }),
        }
      }) ?? []
    )
  }, [dataSheetData?.calibrationSteps, formatMeasurement])

  return (
    <>
      <ReportPage
        tracker={{
          sectionId: RugReportSectionId.DATA_SHEET,
          onRender,
          registerOnce: false,
        }}
        header={<PageHeader title='Inspection Data Sheet' />}
        footer={
          <PageFooter
            jobNumber={mainInfo.assetId}
            revisionNumber={formData.revisionMetadata.revisionNumber}
          />
        }
        contentBottomPadding={24}
        contentStyle={{ marginTop: 0, marginBottom: 0, marginHorizontal: 0 }}>
        <View style={DataSheetStyles.section}>
          <DataSheetTitle>Asset Information</DataSheetTitle>

          <View style={DataSheetStyles.twoColumnLayout}>
            <View style={DataSheetStyles.column}>
              <DataSheetListedItem
                fieldName='Asset:'
                fieldValue={`${mainInfo.assetType} - ${mainInfo.assetId}`}
              />
              <DataSheetListedItem
                fieldName='Material:'
                fieldValue={mainInfo.assetMaterial}
              />
              <DataSheetListedItem
                fieldName='Nominal:'
                fieldValue={nominalRangeStr}
              />
              <DataSheetListedItem
                fieldName='Dimensions:'
                fieldValue={dimensionsStr}
              />
            </View>
            <DataSheetSeparator />
            <View style={DataSheetStyles.column}>
              <DataSheetListedItem
                fieldName='Inspection Surface:'
                fieldValue={dataSheetData?.inspectionSurface}
              />
              <DataSheetListedItem
                fieldName='Surface Condition:'
                fieldValue={dataSheetData?.surfaceCondition}
              />
              <DataSheetListedItem
                fieldName='Coating:'
                fieldValue={
                  dataSheetData?.coatingPresent === true
                    ? 'Yes'
                    : dataSheetData?.coatingPresent === false
                      ? 'No'
                      : undefined
                }
              />
              <DataSheetListedItem
                fieldName='Temperature:'
                fieldValue={mainInfo.inspectionTemperature}
              />
            </View>
          </View>
        </View>

        <View style={DataSheetStyles.section}>
          <DataSheetTitle>Inspection Procedure</DataSheetTitle>
          <DataSheetListedItem
            fieldName='Procedure:'
            fieldValue={dataSheetData?.procedure}
          />
          <DataSheetListedItem
            fieldName='Procedure Title:'
            fieldValue={dataSheetData?.procedureTitle}
          />
        </View>

        <View style={DataSheetStyles.twoColumnLayout}>
          <View style={DataSheetStyles.column}>
            {/* Inspection Equipment */}
            <View style={DataSheetStyles.section}>
              <DataSheetTitle>Inspection Equipment</DataSheetTitle>
              <DataSheetListedItem
                fieldName='Number of Robots:'
                fieldValue={dataSheetData?.robots?.length.toString()}
              />
              <DataSheetListedItem
                fieldName='Robot/DAC Model(s):'
                fieldValue={dataSheetData?.robots
                  ?.map((r) => r.model)
                  .join(', ')}
              />
              <DataSheetListedItem
                fieldName='Robot/DAC S/N(s):'
                fieldValue={dataSheetData?.robots
                  ?.map((r) => r.serialNumber)
                  .join(', ')}
              />
            </View>

            <View style={DataSheetStyles.section}>
              <DataSheetTitle>Calibration Standard</DataSheetTitle>
              <DataSheetListedItem
                fieldName='Type:'
                fieldValue={dataSheetData?.calibrationBlocks
                  ?.map((b) => b.type)
                  .filter(Boolean)
                  .join(', ')}
              />
              <DataSheetListedItem
                fieldName='Material:'
                fieldValue={mainInfo.assetMaterial}
              />
              <DataSheetListedItem
                fieldName='S/N:'
                fieldValue={dataSheetData?.calibrationBlocks
                  ?.map((b) => b.serialNumber)
                  .filter(Boolean)
                  .join(', ')}
              />
            </View>

            <View style={DataSheetStyles.section}>
              <DataSheetTitle>Calibration Verification</DataSheetTitle>
              {dataSheetData?.calibrationTimes &&
                dataSheetData.calibrationTimes.length > 0 && (
                  <>
                    <View style={DataSheetStyles.calibrationHeader}>
                      <Text style={DataSheetStyles.calibrationColumn}>
                        Date
                      </Text>
                      <Text style={DataSheetStyles.calibrationColumn}>
                        Time
                      </Text>
                    </View>
                    {dataSheetData.calibrationTimes.map(
                      (calibrationTime, idx) => {
                        const timeValue = dayjs.isDayjs(calibrationTime)
                          ? calibrationTime
                          : dayjs(calibrationTime)
                        return (
                          <View
                            key={idx}
                            style={DataSheetStyles.calibrationHeader}>
                            <Text
                              style={[
                                DataSheetStyles.calibrationColumn,
                                DataSheetStyles.calibrationValue,
                              ]}>
                              {timeValue.format('MM/DD/YYYY')}
                            </Text>
                            <Text
                              style={[
                                DataSheetStyles.calibrationColumn,
                                DataSheetStyles.calibrationValue,
                              ]}>
                              {timeValue.format('HH:mm a')}
                            </Text>
                          </View>
                        )
                      }
                    )}
                  </>
                )}
            </View>
          </View>

          <DataSheetSeparator />

          <View style={DataSheetStyles.column}>
            <View style={DataSheetStyles.section}>
              <DataSheetListedItem
                fieldName='Inspection Software:'
                fieldValue={softwareVersion}
              />
              <DataSheetListedItem
                fieldName='Analysis Software:'
                fieldValue='UT Scope'
              />
              <DataSheetListedItem
                fieldName='Measurement Mode:'
                fieldValue='Mode 3 (BW2-BW1)'
              />
            </View>

            <View style={DataSheetStyles.section}>
              <View style={DataSheetStyles.calibrationHeader}>
                <Text style={DataSheetStyles.calibrationTitle}>
                  Calibration
                </Text>
                <Text style={DataSheetStyles.calibrationColumn}>Distance</Text>
                <Text style={DataSheetStyles.calibrationColumn}>Amplitude</Text>
              </View>
              {calibrationSteps.map((step, index) => {
                return (
                  <View key={index} style={DataSheetStyles.calibrationHeader}>
                    <Text style={DataSheetStyles.calibrationColumn}>
                      {step.stepName} Step:
                    </Text>
                    <Text
                      style={[
                        DataSheetStyles.calibrationColumn,
                        DataSheetStyles.calibrationValue,
                      ]}>
                      {step.distanceMeasurement}
                    </Text>
                    <Text
                      style={[
                        DataSheetStyles.calibrationColumn,
                        DataSheetStyles.calibrationValue,
                      ]}>
                      80% ±6dB
                    </Text>
                  </View>
                )
              })}
            </View>

            <View style={{ marginBottom: 8 }}>
              <Image src={toka4Image} style={{ maxWidth: '75%' }} />
            </View>
          </View>
        </View>

        <View style={{ marginBottom: 4 }}>
          <DataSheetTitle>Sensor Payload</DataSheetTitle>
          <View style={DataSheetStyles.twoColumnLayout}>
            <View style={DataSheetStyles.column}>
              <DataSheetListedItem
                fieldName='Payload Type:'
                fieldValue={payloadTypesStr}
              />
              <DataSheetListedItem
                fieldName='Sensor Frequency:'
                fieldValue={sensorFrequenciesStr}
              />
              <DataSheetListedItem
                fieldName='Sensor Diameter:'
                fieldValue={sensorDiametersStr}
              />
            </View>
            <DataSheetSeparator />
            <View style={DataSheetStyles.column}>
              <DataSheetListedItem
                fieldName='Angle:'
                fieldValue={
                  sensorAngle !== undefined ? `${sensorAngle}°` : undefined
                }
              />
              <DataSheetListedItem
                fieldName='Wave Mode:'
                fieldValue='Longitudinal'
              />
              <DataSheetListedItem
                fieldName='Couplant Type:'
                fieldValue='Water'
              />
            </View>
          </View>
        </View>

        <View style={DataSheetStyles.section}>
          {sensorPayloadSerialNumbers.map((serialNumbers, i) => (
            <PayloadSerialNumbers
              key={i}
              serialNumbers={serialNumbers}
              index={i + 1}
            />
          ))}
        </View>
      </ReportPage>
      <AddendumPages
        sectionId={RugReportSectionId.DATA_SHEET}
        sectionTitle='Inspection Data Sheet'
      />
    </>
  )
}

type FormDataSheetSectionData = Omit<
  DataSheetSectionData,
  'calibrationBlocks' | 'calibrationTimes' | 'sensorSerialNumbers'
> & {
  calibrationBlocks: CalibrationBlock[]
  calibrationTimes: dayjs.Dayjs[]
  sensorSerialNumbers: PayloadSerialNumbers[]
}

function parseSerialNumbers(text: string) {
  const regex = /\bU[A-Z0-9]+\b/g
  const matches = text.match(regex) || []
  return matches
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<FormDataSheetSectionData>()
  const { formData, handleDataSheetSectionFinish, inspections } =
    useRugReportOps()
  const [parserModalVisible, setParserModalVisible] = React.useState(false)
  const [parserText, setParserText] = React.useState('')

  const inspectionType = useMemo(() => {
    const inspectionTypes =
      inspections?.map((inspection) => inspection.data.inspection_type) ?? []
    return inspectionTypes.at(0) ?? 'rug'
  }, [inspections])

  const initialValues = React.useMemo(() => {
    // Convert sensorSerialNumbers from object to array format for Form.List
    const sensorSerialNumbersArray: PayloadSerialNumbers[] = []
    if (formData.dataSheetSectionData?.sensorSerialNumbers) {
      const sensorSerialNumbers =
        formData.dataSheetSectionData.sensorSerialNumbers
      Object.keys(sensorSerialNumbers)
        .sort((a, b) => Number(a) - Number(b))
        .forEach((key) => {
          sensorSerialNumbersArray.push({
            serialNumbers: sensorSerialNumbers[Number(key)] || [],
          })
        })
    }

    const calibrationTimes = formData.dataSheetSectionData?.calibrationTimes
      ? formData.dataSheetSectionData.calibrationTimes.map((time) =>
          dayjs(time)
        )
      : []

    return {
      inspectionSurface: formData.dataSheetSectionData?.inspectionSurface,
      surfaceCondition: formData.dataSheetSectionData?.surfaceCondition,
      coatingPresent: formData.dataSheetSectionData?.coatingPresent ?? false,
      procedure:
        formData.dataSheetSectionData?.procedure ??
        METHOD_TO_PROCEDURE[inspectionType],
      procedureTitle:
        formData.dataSheetSectionData?.procedureTitle ??
        METHOD_TO_PROCEDURE_TITLE[inspectionType],
      inspectionSoftware:
        formData.dataSheetSectionData?.inspectionSoftware ?? 'Fulcrum',
      payloads:
        (formData.dataSheetSectionData?.payloads?.length ?? 0) > 0
          ? formData.dataSheetSectionData?.payloads
          : DEFAULT_PAYLOADS,
      calibrationBlocks: formData.dataSheetSectionData?.calibrationBlocks || [],
      calibrationTimes,
      calibrationSteps:
        (formData.dataSheetSectionData?.calibrationSteps?.length ?? 0) > 0
          ? formData.dataSheetSectionData?.calibrationSteps
          : DEFAULT_CALIBRATION_STEPS,
      robots: formData.dataSheetSectionData?.robots || [],
      sensorSerialNumbers: sensorSerialNumbersArray,
    }
  }, [formData.dataSheetSectionData, inspectionType])

  const handleParse = React.useCallback(() => {
    const serialNumbers = parseSerialNumbers(parserText)

    if (serialNumbers.length === 0) {
      Modal.warning({
        title: 'No Serial Numbers Found',
        content:
          'No serial numbers matching the pattern were found in the text.',
      })
      return
    }

    const currentSerialNumbers = form.getFieldValue('sensorSerialNumbers') || []

    const chunks: PayloadSerialNumbers[] = []
    for (
      let i = 0;
      i < serialNumbers.length;
      i += DEFAULT_SERIAL_NUMBERS_COUNT
    ) {
      const chunk = serialNumbers.slice(i, i + DEFAULT_SERIAL_NUMBERS_COUNT)
      const paddedChunk = [
        ...chunk,
        ...Array(DEFAULT_SERIAL_NUMBERS_COUNT - chunk.length).fill(''),
      ]
      chunks.push({ serialNumbers: paddedChunk })
    }

    form.setFieldsValue({
      sensorSerialNumbers: [...currentSerialNumbers, ...chunks],
    })

    setParserModalVisible(false)
    setParserText('')
  }, [parserText, form])

  const handleFinish = React.useCallback(
    async (values: FormDataSheetSectionData) => {
      const sensorSerialNumbers: DataSheetSectionData['sensorSerialNumbers'] =
        {}

      if (
        values.sensorSerialNumbers &&
        Array.isArray(values.sensorSerialNumbers)
      ) {
        values.sensorSerialNumbers.forEach(
          (item: { serialNumbers: string[] }, index: number) => {
            sensorSerialNumbers[index] = item.serialNumbers || []
          }
        )
      }

      const calibrationTimes: TimeString[] =
        values.calibrationTimes?.map((time) => {
          if (dayjs.isDayjs(time)) {
            return time.toISOString() as TimeString
          }
          return time
        }) || []

      const processedValues: DataSheetSectionData = {
        ...values,
        sensorSerialNumbers,
        calibrationBlocks: values.calibrationBlocks || [],
        calibrationTimes,
        payloads: values.payloads || [],
      }

      await handleDataSheetSectionFinish(processedValues)
    },
    [handleDataSheetSectionFinish]
  )

  return (
    <StyledForm
      form={form}
      layout='vertical'
      onFinish={handleFinish}
      initialValues={initialValues}>
      <Form.Item
        name='inspectionSurface'
        label='Inspection Surface'
        rules={[
          { required: true, message: 'Please select inspection surface' },
        ]}>
        <AutoComplete
          options={Object.values(ReportInspectionSurface).map((status) => {
            return { value: status }
          })}
          placeholder='Select or Type Surface'
          filterOption={autocompleteFilterOption}
        />
      </Form.Item>

      <Form.Item
        name='surfaceCondition'
        label='Surface Condition'
        rules={[
          { required: true, message: 'Please describe surface condition' },
        ]}>
        <AutoComplete
          options={Object.values(ReportSurfaceCondition).map((status) => {
            return { value: status }
          })}
          placeholder='Select or Type Condition'
          filterOption={autocompleteFilterOption}
        />
      </Form.Item>

      <Form.Item
        name='coatingPresent'
        label='Coating Present'
        rules={[{ required: true }]}>
        <Radio.Group>
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name='procedure' label='Procedure'>
        <Input placeholder='e.g., GR-TOKA-001-FRC Rev.0' />
      </Form.Item>

      <Form.Item name='procedureTitle' label='Procedure Title'>
        <Input placeholder='e.g., General Rapid Ultrasonic Gridding Using TOKA Robots' />
      </Form.Item>

      <Form.Item name='inspectionSoftware' label='Inspection Software'>
        <AutoComplete
          options={INSPECTION_SOFTWARE_OPTIONS}
          placeholder='Select or Type Inspection Software'
          filterOption={autocompleteFilterOption}
        />
      </Form.Item>

      <Form.Item label='Sensor Payloads'>
        <Form.List name='payloads'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <PayloadGroupWrapper key={key}>
                  <PayloadCard direction='vertical'>
                    <PayloadCardHeader>
                      <PayloadCardTitle>Payload {name + 1}</PayloadCardTitle>
                      <RemoveButton onClick={() => remove(name)} />
                    </PayloadCardHeader>
                    <CompactFormItem
                      {...restField}
                      name={[name, 'payloadType']}
                      label='Payload Type'>
                      <AutoComplete
                        options={Object.values(SensorPayloadType).map(
                          (status) => {
                            return { value: status }
                          }
                        )}
                        placeholder='Select or Type Payload Type'
                        filterOption={autocompleteFilterOption}
                      />
                    </CompactFormItem>
                    <CompactFormItem
                      {...restField}
                      name={[name, 'sensorFrequency']}
                      label='Sensor Frequency (MHz)'>
                      <PayloadFrequencyInput
                        placeholder='e.g., 5, 7.5, 10'
                        min={0}
                        step={0.5}
                      />
                    </CompactFormItem>
                    <FlushFormItem label='Sensor Diameter'>
                      <MeasurementInput
                        valueName={[name, 'sensorDiameter', 'value']}
                        unitName={[name, 'sensorDiameter', 'unit']}
                        unitOptions={DISTANCE_UNIT_OPTIONS}
                        placeholder='e.g., 0.125'
                        min={0}
                        step={0.001}
                        precision={3}
                        defaultUnit={GeckoMeasurementUnit.Inches.id}
                      />
                    </FlushFormItem>
                  </PayloadCard>
                </PayloadGroupWrapper>
              ))}
              <Form.Item>
                <ActionButton
                  onClick={() =>
                    add({
                      sensorDiameter: {
                        unit: GeckoMeasurementUnit.Inches.id,
                      },
                    })
                  }>
                  + Add Payload
                </ActionButton>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item label='Calibration Blocks'>
        <Form.List name='calibrationBlocks'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <InlineFormRow key={key}>
                  <Form.Item {...restField} name={[name, 'type']} noStyle>
                    <AutoComplete
                      style={{ width: '17.5rem' }}
                      options={Object.values(CalibrationBlockType).map(
                        (status) => {
                          return { value: status }
                        }
                      )}
                      placeholder='Type'
                      filterOption={autocompleteFilterOption}
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'serialNumber']}
                    noStyle>
                    <CalibrationBlockSerialInput placeholder='Serial Number' />
                  </Form.Item>
                  <RemoveButtonWithPadding onClick={() => remove(name)} />
                </InlineFormRow>
              ))}
              <Form.Item>
                <ActionButton onClick={() => add()}>
                  + Add Calibration Block
                </ActionButton>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item label='Calibration Times'>
        <Form.List name='calibrationTimes'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <CalibrationTimeRow key={`space-${key}`} align='baseline'>
                  <Form.Item {...restField} name={name} key={`form-${key}`}>
                    <DatePicker showTime={{ format: 'HH:mm' }} />
                  </Form.Item>
                  <CloseOutlined onClick={() => remove(name)} />
                </CalibrationTimeRow>
              ))}
              <Form.Item>
                <ActionButton onClick={() => add()}>
                  + Add Calibration Time
                </ActionButton>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item label='Calibration Steps'>
        <Form.List name='calibrationSteps'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <InlineFormRow key={key}>
                  <Form.Item {...restField} name={[name, 'stepName']} noStyle>
                    <AutoComplete
                      style={{ width: '8.75rem' }}
                      placeholder='Step Name'
                      options={CALIBRATION_STEP_OPTIONS}
                      filterOption={autocompleteFilterOption}
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'distance', 'value']}
                    noStyle>
                    <DistanceValueInput placeholder='Value' precision={3} />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'distance', 'unit']}
                    noStyle>
                    <UnitSelect
                      placeholder='Unit'
                      options={DISTANCE_UNIT_OPTIONS}
                    />
                  </Form.Item>
                  <RemoveIcon size={18} onClick={() => remove(name)} />
                </InlineFormRow>
              ))}
              <Form.Item>
                <ActionButton onClick={() => add()}>+ Add Step</ActionButton>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item label='Robots'>
        <Form.List name='robots'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <PayloadGroupWrapper key={key}>
                  <InlineFieldGroup>
                    <InlineFormItem
                      {...restField}
                      key={`model-${key}`}
                      name={[name, 'model']}>
                      <AutoComplete
                        options={ROBOT_MODEL_OPTIONS}
                        placeholder='Robot Model'
                        filterOption={autocompleteFilterOption}
                      />
                    </InlineFormItem>
                    <InlineFormItem
                      {...restField}
                      key={`serialNumber-${key}`}
                      name={[name, 'serialNumber']}>
                      <Input placeholder='Robot S/N' />
                    </InlineFormItem>
                    <RemoveButtonWithPadding onClick={() => remove(name)} />
                  </InlineFieldGroup>
                </PayloadGroupWrapper>
              ))}
              <Form.Item>
                <ActionButton onClick={() => add({ model: 'toka_5' })}>
                  + Add Robot
                </ActionButton>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item label='Sensor Serial Numbers'>
        <Form.List name='sensorSerialNumbers'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }) => (
                <PayloadGroupWrapper key={key}>
                  <PayloadCard direction='vertical'>
                    <PayloadCardHeader>
                      <PayloadCardTitle>Robot {name + 1}</PayloadCardTitle>
                      <RemoveButton onClick={() => remove(name)} />
                    </PayloadCardHeader>
                    <Form.List name={[name, 'serialNumbers']}>
                      {(subFields, subOpt) => (
                        <VerticalStack>
                          {subFields.map((subField, index) => (
                            <Space.Compact key={subField.key}>
                              <SerialNumberLabel>
                                {index + 1}.
                              </SerialNumberLabel>
                              <InlineFormItem
                                {...subField}
                                name={[subField.name]}>
                                <Input
                                  placeholder={`Serial Number ${index + 1}`}
                                />
                              </InlineFormItem>
                              <RemoveButtonWithPadding
                                onClick={() => subOpt.remove(subField.name)}
                              />
                            </Space.Compact>
                          ))}
                          <ActionButton onClick={() => subOpt.add()}>
                            + Add Serial Number
                          </ActionButton>
                        </VerticalStack>
                      )}
                    </Form.List>
                  </PayloadCard>
                </PayloadGroupWrapper>
              ))}
              <Form.Item>
                <VerticalStack>
                  <CantileverButton onClick={() => setParserModalVisible(true)}>
                    Open Parser
                  </CantileverButton>
                  <CantileverButton
                    onClick={() => {
                      add({
                        serialNumbers: Array(DEFAULT_SERIAL_NUMBERS_COUNT).fill(
                          ''
                        ),
                      })
                    }}>
                    + Add Robot Serial Numbers
                  </CantileverButton>
                </VerticalStack>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item>
        <ActionButton primary onClick={() => form.submit()}>
          Save Inspection Data Sheet
        </ActionButton>
      </Form.Item>

      <Modal
        title='Serial Number Parser'
        open={parserModalVisible}
        onCancel={() => {
          setParserModalVisible(false)
          setParserText('')
        }}
        footer={[
          <CantileverButton
            key='cancel'
            onClick={() => {
              setParserModalVisible(false)
              setParserText('')
            }}>
            Cancel
          </CantileverButton>,
          <CantileverButton key='parse' primary onClick={handleParse}>
            Parse
          </CantileverButton>,
        ]}
        width={800}>
        <ParserModalDescription>
          <p>
            Paste text containing serial numbers below. Serial numbers starting
            with &quot;U&quot; followed by letters and numbers will be
            extracted.
          </p>
        </ParserModalDescription>
        <ParserTextArea
          value={parserText}
          onChange={(e) => setParserText(e.target.value)}
          placeholder='Paste your text here...'
          rows={15}
        />
      </Modal>
    </StyledForm>
  )
}

export const InspectionDataSheetSection: ReportSection = {
  id: RugReportSectionId.DATA_SHEET,
  title: 'Inspection Data Sheet',
  description: 'Technical specifications of the inspection',
  required: true,
  RenderPDF: RenderPDF,
  RenderReact: RenderReact,
  requiredFields: [
    'dataSheetSectionData.inspectionSurface',
    'dataSheetSectionData.surfaceCondition',
    'dataSheetSectionData.coatingPresent',
    'dataSheetSectionData.calibrationBlocks',
  ],
}

interface PayloadSerialNumbersProps {
  serialNumbers: string[]
  index: number
}

const PayloadSerialNumbers: React.FC<PayloadSerialNumbersProps> = ({
  serialNumbers,
  index,
}) => {
  const chunkLength = 6
  const chunkedSerialNumbers = chunk(serialNumbers, chunkLength)

  return (
    <View wrap={false} style={DataSheetStyles.payloadContainer}>
      <View style={DataSheetStyles.payloadTitle}>
        <Text>Payload #{index}</Text>
      </View>

      <View style={DataSheetStyles.tableContainer}>
        {chunkedSerialNumbers.map((chunkArray, i) => (
          <View
            key={i}
            style={
              i === chunkedSerialNumbers.length - 1
                ? DataSheetStyles.tableColumn
                : DataSheetStyles.tableColumnWithBorder
            }>
            <View key={`header-${i}`} style={DataSheetStyles.tableHeaderRow}>
              <View style={DataSheetStyles.tableDataRow}>
                <Text style={DataSheetStyles.tableHeader}>Sensor #</Text>
                <Text style={DataSheetStyles.tableHeaderAccent}>
                  Sensor S/N
                </Text>
              </View>
            </View>
            {chunkArray.map((serialNumber, j) => (
              <View
                key={`${index}-${i * chunkLength + j}`}
                style={DataSheetStyles.tableDataRow}>
                <Text style={DataSheetStyles.tableCell}>
                  {i * chunkLength + j + 1}
                </Text>
                <Text style={DataSheetStyles.tableCellAccent}>
                  {serialNumber}
                </Text>
              </View>
            ))}
          </View>
        ))}
      </View>
    </View>
  )
}
